<template>
  <section class="container course-details">
    <form v-if="prices.length > 0" @submit.prevent="submit">
      <div class="row no-gutters two-selectors">
        <div class="col-12 col-sm-6">
          <div class="form-group">
            <label for="language">
              <strong>{{ strings.course_language }}</strong>
            </label>
            <Select2 
                id="language"
                v-model="activeLanguage"
                class="select-type-one form-control"
                :options="languages.map(({ code, name }) => ({ id: code, text: name }))"
                :settings="{ minimumResultsForSearch: -1 }"
                @select="setSchools"
              />
          </div>
        </div>

        <div class="col-12 col-sm-6">
          <div id="school" class="form-group">
            <label for="school-selection">
              <strong>
                {{ activeSchoolName }}
                {{ strings.prices_from }}
                <span>{{ fromPrice }}</span>
              </strong>
            </label>

            <Select2
                id="school-selection"
                v-model="activeSchool"
                :options="schools.map(d => ({ id: d.id, text: d.name, disabled: d.disabled }))"
                class="select-type-one form-control"
                :settings="{ minimumResultsForSearch: -1 }"
                @select="handleSchool"
              />
          </div>
        </div>
      </div>

      <div class="mbsc-form-group form-group date-selection">
        <label>
          <strong>{{ strings.course_dates }}</strong>
          <div>
            <select id="dates-selection" v-model="activeCourse" name="dates-selection" />
          </div>
        </label>
      </div>

      <fade-transition>
        <alert v-if="alertInfo"
            class="price-alert mt-2"
            icon="fa-exclamation-triangle"
            :type="alertInfo.type"
            :title="alertInfo.title"
          >
          {{ alertInfo.info }}
          <template v-if="alertInfo.full">
            <a v-b-modal.reserve-list>{{ strings.waiting_list }}</a>.
          </template>
        </alert>
      </fade-transition>

      <template v-if="activePrice">
        <div class="row main-price">
          <div class="col-8">
            <span class="label">{{ strings.price }}</span>
          </div>

          <div class="col-4 text-right">
            <span id="main-price" class="price">{{ formatMoney(activePrice.price) }}</span>
          </div>
        </div>

        <fade-transition>
          <alert v-if="activePrice.installment" class="price-installment mt-2" type="dark">
            <div class="explanation">
              {{ strings.installments_desc }}
            </div>
            <div class="d-flex justify-content-between align-items-center">
              <span class="label">{{ strings.installments_price }}</span>
              <span class="price ml-2" style="white-space:nowrap">{{ formatMoney(activePrice.installment) }}</span>
            </div>
          </alert>
        </fade-transition>

        <div v-if="activePrice.detail" class="price-detail mt-2" v-html="activePrice.detail" />

        <what-price-cover :usp_data="usp_data" :strings="strings" />

        <div class="price-daytime mt-2 d-flex flex-row">
          <i class="fa fa-calendar-alt pr-2" style="color: #aaa" />
          <div>
            <div v-for="(item, key) in activePrice.days_times" :key="key" class="daytime">
              {{ item }}
            </div>
          </div>
        </div>

        <button v-if="activePrice.supply !== status.FULL" class="btn button yellow mt-3">
          {{ strings.continue }}
        </button>
      </template>
    </form>

    <!-- Reserve list Modal (start) -->
    <b-modal id="reserve-list" ref="reserveModal" :title="strings.reserve_add" hide-footer @shown="modalShown">
      <p>{{ strings.reserve_info }}</p>

      <form @submit.prevent="submitReserve">
        <div class="form-group">
          <input id="Name"
              v-model="reserve.name"
              type="text"
              class="form-control"
              :placeholder="strings.your_name"
              :required="true"
            >
        </div>

        <div class="form-group">
          <input id="Email"
              v-model="reserve.email"
              type="email"
              class="form-control"
              :placeholder="strings.your_email"
              required
            >
        </div>

        <div class="form-group">
          <div class="form-control p-0">
            <vue-tel-input ref="phoneInput"
                :placeholder="strings.your_phonenumber"
                :default-country="reserve.phoneCountry"
                :required="true"
                style="border: none;"
                @onInput="handlePhone"
              />
            <div v-if="!reserve.phoneValid" class="invalid-feedback d-block">
              {{ strings.phone_invalid }}
            </div>
          </div>
        </div>

        <button :disabled="!reserve.phoneValid" type="submit" class="button blue-full mt-3">
          {{ strings.submit }}
        </button>

        <span v-for="(error, key) in reserve.errors.non_field_errors" :key="key" class="d-flex mt-1 text-danger">
          {{ error }}
        </span>
      </form>
    </b-modal>
    <!-- Reserve list Modal (end) -->

    <notifications position="bottom left" classes="notification" />
  </section>
</template>

<script>
import moment from 'moment'
import { scrollTo } from 'vue-scrollto'
import mobiscroll from '@mobiscroll/javascript'
import '@mobiscroll/javascript/dist/css/mobiscroll.min.css'
import { sendGTMEvent } from '@/utils/utils'
import { BModal, VBModal } from 'bootstrap-vue'
import Alert from '@/components/form/Alert'
import FadeTransition from '@/components/form/FadeTransition'
import WhatPriceCover from '@/components/partials/WhatPriceCover'

const unique = (arr, key) => {
  return arr.map(a => a[key]).filter((x, i, a) => a.indexOf(x) == i)
}


const logError = err => {
  // eslint-disable-next-line no-console
  console.log(
    '%cErrors:',
    'background: red; color: white; border-radius: 2px; padding: 2px;',
    err.response.data
  )
}

export default {
  name: 'CourseDetails',
  components: {
    WhatPriceCover,
    FadeTransition,
    Alert,
    'b-modal': BModal
  },
  directives: {
    // Note that Vue automatically prefixes directive names with `v-`
    'b-modal': VBModal
  },
  data: () => {
    return {
      book_types: [],
      languages: [],
      schools: [],
      from_prices: [],
      strings: {},
      usp_data: null,

      status: {
        AVAILABLE: 1,
        LIMITED: 2,
        FULL: 3
      },

      reserve: {
        name: '',
        email: '',
        phone: '',
        phoneValid: true,
        phoneCountry: 'gb',
        errors: {
          non_field_errors: []
        }
      },

      dateScroll: null,
      availableCourses: [],
      activeCourse: null,
      activeSchool: '', // str:Date, example '2019-11-25'
      activeLanguage: '' // str:Language code, example 'en'
    }
  },

  computed: {
    fromPrice() {
      const price = this.prices
        .filter(p => p.language.code === this.activeLanguage)
        .filter(p => p.school === parseInt(this.activeSchool))
        .reduce(
          (res, { amount, currency }) => {
            if (!res.amount || res.amount > amount) {
              res = { amount, currency }
            }
            return res
          },
          { amount: null }
        )

      if (price.amount) return this.formatMoney(price)
      return 'No prices exist'
    },

    activeSchoolName() {
      const dest = this.schools.find(d => d.id === parseInt(this.activeSchool))
      if (dest) {
        return dest.name
      }
      return ''
    },

    activePrice() {
      return this.availableCourses.find(c => c.day_time === this.activeCourse)
    },

    alertInfo() {
      if (this.activePrice) {
        if (this.activePrice.supply === this.status.FULL) {
          return {
            full: true,
            type: 'primary',
            title: this.strings.course_full,
            info: this.strings.course_waiting
          }
        } else if (this.activePrice.supply === this.status.LIMITED) {
          return {
            full: false,
            type: 'secondary',
            title: this.strings.course_limited,
            info: this.strings.course_limited_info
          }
        }
      }
      return null
    },

    languageCodes() {
      return this.languages.map(({ code }) => code)
    }
  },

  watch: {
    // eslint-disable-next-line no-unused-vars
    activePrice: function (course) {
      this.initialized && scrollTo('#school', 700, { offset: -20 })
      this.initialized = true
      this.axios
        .post(window.location.pathname, {
          eec: true,
          course: course.course_id,
          price: course.price,
        })
        .then(res => {
          sendGTMEvent(res.data)
        })
    }
  },
  created() {
    const data = JSON.parse(document.getElementById('data').textContent)
    const { schools, prices, language, progress, query, ...other } = data

    const locale = language.replace('no', 'nb')

    this.prices = prices.map(price => {
      price.year = moment(price.start).year()
      const start = moment(price.start).locale(locale).format('DD MMM')
      const end = moment(price.end).locale(locale).format('DD MMM')
      price.label = `${start} - ${end} (${price.time})`
      price.day_time = `${price.start}_${price.time.split(' - ')[0]}`
      return price
    })

    this.language = language

    prices.forEach(p => {
      if (!this.languages.some(l => l.code === p.language.code)) {
        this.languages.push(p.language)
      }
    })

    this.activeLanguage =
      this.languages.map(l => l.code).find(c => c === language) ||
      (this.languages.length > 0 ? this.languages[0].code : 'en')

    const availableSchools = unique(prices, 'school')
    this.schools = schools.filter(s => {
      return availableSchools.includes(s.id)
    })

    if (progress) {
      // eslint-disable-next-line no-console
      console.log('progress exists, query not used.')
      if (this.languages.includes(progress.language)) {
        this.activeLanguage = progress.language
      }
      this.activeSchool = progress.school
      const activeCourse = this.prices.find(
        p => p.course_id === progress.course_id
      )
      this.activeCourse = activeCourse && activeCourse.day_time
    } else if (query && Object.keys(query).length) {
      // eslint-disable-next-line no-console
      console.log('query is taken into consideration.')

      const course = query.course && this.prices.find(p => p.course_id == query.course)
      const school = query.school && this.schools.find(s => s.ref === query.school)
      const language = query.language && this.languageCodes.find(c => c === query.language)

      if (course) {
        this.activeCourse = course.day_time
        this.activeLanguage = course.language.code
        this.activeSchool = course.school
      } else if (school) {
        const availableLangauges = unique(this.prices.filter(p => p.school == school.id).map(p => p.language), 'code')
        const selectedLanguage = availableLangauges.includes(language) && language
        this.activeSchool = school.id
        this.activeLanguage = selectedLanguage || (availableLangauges.includes('en') && 'en') || availableLangauges[0]
      } else {
        this.activeLanguage = language || this.activeLanguage
      }
    }

    // Add other fields for convenience...
    Object.keys(other).forEach(key => (this[key] = data[key]))
  },

  mounted() {
    this.dateScroll = mobiscroll.select('#dates-selection', {
      display: 'inline',
      showInput: false,
      data: [],
      theme: 'ios',
      group: {
        groupWheel: false,
        header: true,
        clustered: false
      }
    })
    this.setSchools()
    this.reserve.phoneCountry = this.country_code
  },

  methods: {
    formatMoney(price) {
      return new Intl.NumberFormat(this.language, {
        minimumFractionDigits: 0,
        style: 'currency',
        currency: price.currency
      }).format(price.amount)
    },

    getAvailableSchools() {
      return this.prices.reduce((ret, cur) => {
        const languageMatch = cur.language.code === this.activeLanguage
        if (languageMatch && !ret.includes(cur.school)) {
          ret.push(cur.school)
        }
        return ret
      }, [])
    },

    getActiveSchool(ref) {
      return this.prices.find(p => p.course_id === ref).day_time
    },

    setSchools() {
      const available = this.getAvailableSchools()
      this.schools = this.schools.map(school => {
        school.disabled = !available.includes(school.id)
        return school
      })

      if (!available.includes(this.activeSchool)) {
        this.schools.some(school => {
          if (available.includes(school.id)) {
            this.activeSchool = school.id
            return true
          }
        })
      }
      this.setAvailableCourses()
    },

    setAvailableCourses() {
      this.availableCourses = this.prices
        .filter(p => {
          return parseInt(this.activeSchool) === p.school
        })
        .filter(p => {
          return this.activeLanguage === p.language.code
        })

      if (this.availableCourses.length === 0) {
        return null
      }

      this.dateScroll.refresh(
        this.availableCourses.map(d => ({
          text: d.label,
          value: d.day_time,
          group: d.year,
        }))
      )

      if (!this.activeCourse) {
        // eslint-disable-next-line no-console
        console.log('activeCourse not set, choosing first')
        this.dateScroll.setVal(this.availableCourses[0].day_time, true)
      } else {
        if (
          !this.availableCourses.some(c => c.day_time === this.activeCourse)
        ) {
          const course = this.availableCourses.find(
            d => d.start === this.activeCourse.split('_')[0]
          )
          if (course) {
            // eslint-disable-next-line no-console
            console.log('activeCourse course not in select, choosing same date')
            this.dateScroll.setVal(course.day_time, true)
          } else {
            // eslint-disable-next-line no-console
            console.log('activeCourse course not in select, choosing first')
            this.dateScroll.setVal(this.availableCourses[0].day_time, true)
          }
        } else {
          // eslint-disable-next-line no-console
          console.log('Keeping activeCourse as is.')
          this.dateScroll.setVal(this.activeCourse, true)
        }
      }
    },

    handleSchool() {
      this.setAvailableCourses()
    },

    modalShown() {
      const phoneInput = this.$refs.phoneInput
      phoneInput.choose(phoneInput.findCountry(this.country_code))
    },

    handlePhone({ number, isValid }) {
      this.reserve.phone = number.e164
      this.reserve.phoneValid = isValid
    },

    submitReserve() {
      this.axios
        .post(window.location.pathname, {
          reserve: true,
          course: this.activePrice.course_id,
          ...this.reserve
        })
        .then(() => {
          this.$refs.reserveModal.hide()
          this.reserve.errors = []
          this.$notify({
            title: this.strings.reserve_success_title,
            text: this.strings.reserve_success_text,
            type: 'success'
          })
        })
        .catch(err => {
          this.reserve.errors = err.response.data || null
          logError(err)
        })
    },

    submit() {
      this.axios
        .post(window.location.pathname, {
          school: this.activePrice.school,
          language: this.activePrice.language.code,
          course_id: this.activePrice.course_id
        })
        .then(response => {
          window.location = response.data
        })
        .catch(err => logError(err))
    }
  }
}
</script>

<style lang="scss">
.select-type-one.form-control {
  padding: 0;
  width: 100%;
  height: calc(1.5em + 0.75rem + 0.6rem);

  .select2-container {
    height: unset;

    .select2-container--default {
      width: 100%;
      height: 100%;
    }

    .select2-selection--single {
      height: 100%;
      border: none;

      .select2-selection__rendered {
        line-height: calc(1.5em + 0.75rem + 0.4rem);
        font-size: 1rem;
        border: none;
        padding-left: 1rem;
      }

      .select2-selection__arrow {
        padding-right: 2.5rem;
        height: 100%;

        b {
          border-width: 8px 6px 0 6px;
        }
      }
    }
  }
}

.vue-tel-input {
  border: none;
  height: 100%;

  .vti__input {
    background: none;
  }
}

.button.yellow:disabled {
  background: lighten(#ffcd00, 15%);
  cursor: not-allowed;
}

.notification {
  padding: 10px;
  margin: 0 5px 5px;

  font-size: 12px;

  color: #ffffff;
  background: #44a4fc;
  border-left: 5px solid #187fe7;

  .notification-title {
    font-size: 1.2rem;
  }

  .notification-content {
    font-size: 1rem;
  }

  &.warn {
    background: darken(#ffb648, 10%);
    border-left-color: #f78053;
  }

  &.error {
    background: #e54d42;
    border-left-color: #b82e24;
  }

  &.success {
    background: #16a57b;
    border-left-color: #42a85f;
  }
}

.mbsc-form-group {
  margin: 0;
}

#reserve-list___BV_modal_backdrop_ {
  transition: opacity 1s ease-in-out;
  opacity: 0.3;
}
</style>

<style lang="scss" scoped>
@media screen and (max-width: 425px) {
  .date-selection::v-deep {
    .mbsc-sc-itm,
    .mbsc-sc-whl-gr-3d .mbsc-sc-itm-3d {
      font-size: 1.1rem;
    }
  }
}

.date-selection::v-deep .mbsc-sc-whl-l {
  border-top: 1px solid #ccc;
  border-bottom: 1px solid #ccc;
}

.alert {
  font-size: 95%;
  color: #373636;

  &.price-alert {
    background-color: white;

    ::v-deep {
      .alert__title {
        margin-bottom: 0.5rem !important;
      }
      .alert__info {
        color: #989898;
      }
    }

    &.alert-primary {
      ::v-deep {
        .alert__info {
          a {
            cursor: pointer;
            text-decoration: none;
            color: #dd2727;
          }
        }
      }
    }
  }

  &.price-installment {
    border: 1px solid #063456;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;

    .explanation {
      font-size: 0.75rem;
    }
  }
}

.price-detail {
  ::v-deep {
    p {
      margin: 0;
      line-height: 1rem;
    }
  }
}

.price-daytime {
  line-height: 1rem;
}
</style>
